import React, { useState, useRef, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import {
  FaFileCsv, FaFileExcel, FaSearch, FaFilter, FaUserTie,
  FaProjectDiagram, FaFileInvoiceDollar, FaBoxOpen, FaStore, FaTools,
  FaUsers, FaClipboardCheck, FaFolderOpen, FaChevronDown, FaTable,
  FaArrowLeft, FaCalendarAlt, FaColumns, FaChartPie, FaChartLine,
  FaMoneyBillWave, FaTachometerAlt, FaBuilding, FaHardHat
} from "react-icons/fa";
import {
  LineChart, Line, PieChart, Pie, Cell, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from "recharts";
import { API_BASE_URL } from "../api/axiosConfig";

const API = API_BASE_URL;
const token = () => localStorage.getItem("token");
const authHeader = () => ({ Authorization: `Bearer ${token()}` });

// ─────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────
const getLastMonth = () => {
  const d = new Date();
  d.setMonth(d.getMonth() - 1);
  return d.toISOString().slice(0, 7);
};

const exportToCSV = (data, filename, columns) => {
  if (!data?.length) { toast.warn("No data to export"); return; }
  const keys = columns || Object.keys(data[0]);
  const rows = [
    keys.join(','),
    ...data.map(row => keys.map(k => {
      const v = row[k] == null ? '' : String(row[k]);
      return v.includes(',') ? `"${v}"` : v;
    }).join(','))
  ];
  const blob = new Blob([rows.join('\n')], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = `${filename}_${new Date().toISOString().slice(0,10)}.csv`;
  a.click(); URL.revokeObjectURL(url);
  toast.success("CSV exported successfully!");
};

const exportToExcel = (data, filename, columns) => {
  if (!data?.length) { toast.warn("No data to export"); return; }
  const keys = columns || Object.keys(data[0]);
  let tableHTML = '<table border="1"><thead><tr>' + keys.map(k => `<th>${k.replace(/_/g, ' ')}</th>`).join('') + '</tr></thead><tbody>';
  data.forEach(row => {
    tableHTML += '<tr>' + keys.map(k => `<td>${row[k] == null ? '' : row[k]}</td>`).join('') + '</tr>';
  });
  tableHTML += '</tbody></table>';
  const blob = new Blob([tableHTML], { type: 'application/vnd.ms-excel' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = `${filename}_${new Date().toISOString().slice(0,10)}.xls`;
  a.click(); URL.revokeObjectURL(url);
  toast.success("Excel exported successfully!");
};

// ─────────────────────────────────────────────
// CONFIGURATION
// ─────────────────────────────────────────────
const STATUS_OPTIONS = {
  invoices: ["Paid", "Unpaid", "Overdue", "Draft"],
  projects: ["Active", "Completed", "On Hold", "Delayed"],
  tasks: ["Pending", "In Progress", "Completed", "Review"],
  employees: ["Active", "Inactive", "On Leave", "Terminated"],
  vendors: ["Active", "Blacklisted", "Pending"],
  leads: ["New", "Contacted", "Qualified", "Lost", "Won"],
  default: ["Active", "Inactive", "Pending", "Completed"]
};

const REPORT_MODULES = [
  {
    id: "crm", label: "CRM & Sales", desc: "Leads, customers, sales pipeline.",
    color: "bg-blue-500", icon: <FaUserTie className="text-white text-xl" />,
    subReports: [ { id: "leads", label: "Leads Report" }, { id: "customers", label: "Customers List" }, { id: "activities", label: "Activity History" }, { id: "pipeline", label: "Sales Pipeline" } ]
  },
  {
    id: "projects", label: "Projects & Tasks", desc: "Timelines, budgets, task completion.",
    color: "bg-teal-500", icon: <FaProjectDiagram className="text-white text-xl" />,
    subReports: [ { id: "project_status", label: "Project Status" }, { id: "project_budget", label: "Cost vs Budget" }, { id: "task_status", label: "Task Status" }, { id: "overdue_tasks", label: "Overdue Tasks" } ]
  },
  {
    id: "finance", label: "Finance", desc: "P&L, Balance Sheet, Invoices.",
    color: "bg-emerald-600", icon: <FaFileInvoiceDollar className="text-white text-xl" />,
    subReports: [ { id: "profit_loss", label: "Profit & Loss" }, { id: "balance_sheet", label: "Balance Sheet" }, { id: "invoices", label: "Customer Invoices" }, { id: "vendor_bills", label: "Vendor Bills" }, { id: "tax_summary", label: "Tax Summary" } ]
  },
  {
    id: "inventory", label: "Inventory", desc: "Stock, movement, materials.",
    color: "bg-orange-500", icon: <FaBoxOpen className="text-white text-xl" />,
    subReports: [ { id: "stock_summary", label: "Stock Summary" }, { id: "low_stock", label: "Low Stock Alerts" }, { id: "material_purchase", label: "Material Purchases" }, { id: "scrap_report", label: "Scrap & Wastage" } ]
  },
  {
    id: "vendors", label: "Vendors", desc: "Vendor list, PO history.",
    color: "bg-red-500", icon: <FaStore className="text-white text-xl" />,
    subReports: [ { id: "vendor_list", label: "Vendor Directory" }, { id: "po_history", label: "PO History" }, { id: "vendor_outstanding", label: "Outstanding Bills" } ]
  },
  {
    id: "assets", label: "Assets", desc: "Register, depreciation, allocation.",
    color: "bg-purple-600", icon: <FaTools className="text-white text-xl" />,
    subReports: [ { id: "asset_register", label: "Asset Register" }, { id: "asset_maintenance", label: "Maintenance History" }, { id: "asset_allocation", label: "Asset Allocation" } ]
  },
  {
    id: "employees", label: "HR & Workforce", desc: "Payroll, statutory reports, analytics.",
    color: "bg-pink-600", icon: <FaUsers className="text-white text-xl" />,
    isHR: true,
    subReports: [
      { id: "kpi_dashboard",    label: "Director's KPI Dashboard", isChart: true },
      { id: "salary_register",  label: "Salary Register" },
      { id: "pf_statement",     label: "PF Statement" },
      { id: "esi_statement",    label: "ESI Statement" },
      { id: "late_frequency",   label: "Late Frequency Report" },
      { id: "absenteeism",      label: "Absenteeism Trends", isChart: true },
      { id: "labour_cost",      label: "Labour Cost Analytics", isChart: true },
      { id: "employee_list",    label: "Employee Directory" },
      { id: "attendance",       label: "Attendance Log" }
    ]
  },
  {
    id: "compliance", label: "Compliance", desc: "Checklists, expiry alerts.",
    color: "bg-yellow-600", icon: <FaClipboardCheck className="text-white text-xl" />,
    subReports: [ { id: "expiry_alerts", label: "Expiry Alerts" }, { id: "compliance_list", label: "Compliance Checklist" } ]
  },
  {
    id: "documents", label: "Documents", desc: "Files and upload history.",
    color: "bg-cyan-600", icon: <FaFolderOpen className="text-white text-xl" />,
    subReports: [ { id: "document_list", label: "All Documents" }, { id: "upload_history", label: "Upload History" } ]
  }
];

const CHART_COLORS = ["#3b82f6","#ec4899","#10b981","#f59e0b","#8b5cf6","#ef4444","#06b6d4","#84cc16"];
const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n || 0);
const fmtNum = (n) => new Intl.NumberFormat('en-IN').format(n || 0);

// ─────────────────────────────────────────────
// COMPONENT: CELL RENDERER
// ─────────────────────────────────────────────
const renderCell = (val) => {
  if (val === null || val === undefined) return "-";
  if (typeof val === "object") {
    if (val.message) return val.message;
    if (Array.isArray(val)) return val.length > 0 ? `[${val.length} Items]` : "-";
    try { return JSON.stringify(val); } catch { return "Complex Data"; }
  }
  const strVal = String(val);
  if (["Paid", "Active", "Completed", "Won", "Finalized"].includes(strVal))
    return <span className="px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-xs font-bold">{strVal}</span>;
  if (["Unpaid", "Pending", "Overdue", "Lost", "Inactive", "Draft"].includes(strVal))
    return <span className="px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-xs font-bold">{strVal}</span>;
  return strVal;
};

// ─────────────────────────────────────────────
// CHART COMPONENTS
// ─────────────────────────────────────────────
const KpiCard = ({ label, value, sub, icon, color = "blue", change }) => {
  const colorMap = { blue: "bg-blue-50 text-blue-600 border-blue-100", green: "bg-emerald-50 text-emerald-600 border-emerald-100", pink: "bg-pink-50 text-pink-600 border-pink-100", orange: "bg-orange-50 text-orange-600 border-orange-100" };
  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 flex items-start gap-3">
      <div className={`p-2.5 rounded-lg border ${colorMap[color]} flex-shrink-0`}>{icon}</div>
      <div className="min-w-0">
        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider truncate">{label}</p>
        <p className="text-lg font-bold text-gray-800 leading-tight">{value}</p>
        {sub && <p className="text-[10px] text-gray-400 mt-0.5">{sub}</p>}
        {change !== undefined && (
          <p className={`text-[10px] font-semibold mt-0.5 ${parseFloat(change) >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
            {parseFloat(change) >= 0 ? '▲' : '▼'} {Math.abs(change)}% vs last month
          </p>
        )}
      </div>
    </div>
  );
};

const KpiDashboard = ({ month, onMonthChange }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [groupBy, setGroupBy] = useState('department');

  const load = useCallback(async (m) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/analytics/kpi`, { params: { month: m }, headers: authHeader() });
      setData(res.data);
    } catch { toast.error("Failed to load KPI data"); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(month); }, [month, load]);

  if (loading) return <div className="flex justify-center h-64 items-center"><div className="w-8 h-8 border-2 border-pink-500 border-t-transparent rounded-full animate-spin" /></div>;
  if (!data) return null;

  const { kpi, attendance_trend, dept_cost, role_cost } = data;
  const costData = groupBy === 'department' ? dept_cost : role_cost;
  const costKey = groupBy === 'department' ? 'department' : 'role';

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center gap-3">
        <label className="text-xs font-bold text-gray-500 uppercase">Month</label>
        <input type="month" value={month} onChange={e => onMonthChange(e.target.value)} className="border border-gray-300 rounded-md px-3 py-1.5 text-xs focus:ring-1 focus:ring-pink-500 outline-none" />
      </div>
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
        <KpiCard label="Total Payout" value={fmt(kpi.total_payout)} sub={`${fmtNum(kpi.employee_count)} employees`} icon={<FaMoneyBillWave className="text-lg" />} color="blue" change={kpi.month_over_month_change} />
        <KpiCard label="Base Pay" value={fmt(kpi.total_base)} icon={<FaUserTie className="text-lg" />} color="green" />
        <KpiCard label="Overtime" value={fmt(kpi.total_overtime)} icon={<FaChartLine className="text-lg" />} color="orange" />
        <KpiCard label="Total Deductions" value={fmt(kpi.total_deductions)} icon={<FaTachometerAlt className="text-lg" />} color="pink" />
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
          <h3 className="text-xs font-bold text-gray-600 uppercase mb-3 flex items-center gap-2"><FaChartLine className="text-blue-500" /> Attendance % Trend</h3>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={attendance_trend} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="month" tick={{ fontSize: 10 }} />
              <YAxis domain={[0, 100]} tick={{ fontSize: 10 }} unit="%" />
              <Tooltip formatter={(v) => [`${v}%`, 'Attendance']} />
              <Line type="monotone" dataKey="attendance_pct" stroke="#3b82f6" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-xs font-bold text-gray-600 uppercase flex items-center gap-2"><FaChartPie className="text-pink-500" /> Labour Cost Dist.</h3>
            <div className="flex gap-1">
              {['department', 'role'].map(g => (
                <button key={g} onClick={() => setGroupBy(g)} className={`px-2 py-0.5 rounded text-[10px] font-semibold transition ${groupBy === g ? 'bg-pink-100 text-pink-700' : 'text-gray-400 hover:bg-gray-100'}`}>
                  {g === 'department' ? 'Dept' : 'Role'}
                </button>
              ))}
            </div>
          </div>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie data={costData} dataKey="total_cost" nameKey={costKey} cx="50%" cy="50%" outerRadius={75} label={({ name, percent }) => percent > 0.05 ? `${name?.slice(0,10)} ${(percent*100).toFixed(0)}%` : ''}>
                {costData?.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
              </Pie>
              <Tooltip formatter={(v) => fmt(v)} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

const AbsenteeismChart = ({ filters }) => {
  const [data, setData] = useState(null);
  useEffect(() => {
    axios.get(`${API}/api/analytics/reports/absenteeism`, { params: { months: 6, ...filters }, headers: authHeader() })
      .then(r => setData(r.data)).catch(() => toast.error("Failed to load absenteeism data"));
  }, [filters]);

  if (!data) return <div className="flex justify-center h-32 items-center"><div className="w-6 h-6 border-2 border-pink-500 border-t-transparent rounded-full animate-spin" /></div>;
  return (
    <div className="p-4 space-y-4">
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
        <h3 className="text-xs font-bold text-gray-600 uppercase mb-3">Absenteeism % Trend</h3>
        <ResponsiveContainer width="100%" height={200}>
          <LineChart data={data.trend}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="month" tick={{ fontSize: 10 }} />
            <YAxis tick={{ fontSize: 10 }} unit="%" />
            <Tooltip formatter={(v) => [`${v}%`, 'Absent Rate']} />
            <Line type="monotone" dataKey="absenteeism_pct" stroke="#ef4444" strokeWidth={2} dot={{ r: 3 }} />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
        <div className="px-4 py-2 border-b border-gray-100 bg-gray-50"><p className="text-xs font-bold text-gray-500 uppercase">Top Absentees</p></div>
        <table className="min-w-full text-xs text-gray-600">
          <thead className="bg-gray-50 text-[10px] uppercase text-gray-400 font-bold">
            <tr>{['Employee', 'Department', 'Role', 'Absent Days'].map(h => <th key={h} className="px-4 py-2 text-left">{h}</th>)}</tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {data.top_absentees.map((r, i) => (
              <tr key={i} className="hover:bg-red-50/30">
                <td className="px-4 py-2 font-medium text-gray-800">{r.name}</td>
                <td className="px-4 py-2">{r.department}</td>
                <td className="px-4 py-2">{r.role}</td>
                <td className="px-4 py-2"><span className="px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-bold">{r.absent_days}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const LabourCostChart = ({ filters }) => {
  const [data, setData] = useState(null);
  const [groupBy, setGroupBy] = useState('department');

  useEffect(() => {
    axios.get(`${API}/api/analytics/labour-cost`, { params: { ...filters, groupBy }, headers: authHeader() })
      .then(r => setData(r.data)).catch(() => toast.error("Failed to load labour cost data"));
  }, [filters, groupBy]);

  if (!data) return <div className="flex justify-center h-32 items-center"><div className="w-6 h-6 border-2 border-pink-500 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="p-4 space-y-4">
      <div className="flex gap-2 items-center">
        <span className="text-xs text-gray-500 font-bold uppercase">Group By:</span>
        {['department', 'role'].map(g => (
          <button key={g} onClick={() => setGroupBy(g)} className={`px-3 py-1 rounded text-xs font-semibold transition ${groupBy === g ? 'bg-pink-600 text-white' : 'bg-white border border-gray-300 text-gray-600 hover:bg-gray-50'}`}>
            {g === 'department' ? 'Department' : 'Role'}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-3 text-center">
          <p className="text-[10px] text-gray-400 uppercase font-bold">Total Cost</p>
          <p className="text-xl font-bold text-pink-600">{fmt(data.summary?.total_cost)}</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-3 text-center">
          <p className="text-[10px] text-gray-400 uppercase font-bold">Total Headcount</p>
          <p className="text-xl font-bold text-gray-800">{data.summary?.total_headcount}</p>
        </div>
      </div>
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={data.data} margin={{ top: 5, right: 10, left: 10, bottom: 30 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="group_name" tick={{ fontSize: 9 }} angle={-30} textAnchor="end" />
            <YAxis tick={{ fontSize: 10 }} tickFormatter={v => `₹${(v/1000).toFixed(0)}k`} />
            <Tooltip formatter={(v, n) => [fmt(v), n.replace(/_/g,' ')]} />
            <Legend iconSize={10} wrapperStyle={{ fontSize: 10 }} />
            <Bar dataKey="total_base" name="Base Pay" fill="#3b82f6" stackId="a" />
            <Bar dataKey="total_overtime" name="Overtime" fill="#f59e0b" stackId="a" />
            <Bar dataKey="total_bonus" name="Bonus" fill="#10b981" stackId="a" radius={[4,4,0,0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────
// HR REPORT COMPONENT 
// ─────────────────────────────────────────────
const HR_REPORT_ENDPOINTS = {
  salary_register: '/api/analytics/reports/salary-register',
  pf_statement:    '/api/analytics/reports/pf',
  esi_statement:   '/api/analytics/reports/esi',
  late_frequency:  '/api/analytics/reports/late-frequency',
};

const HRReportTable = ({ reportId }) => {
  const [data, setData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hiddenCols, setHiddenCols] = useState([]);
  const [showColMenu, setShowColMenu] = useState(false);
  const colMenuRef = useRef(null);

  const [month, setMonth] = useState(getLastMonth());
  const [department, setDepartment] = useState('');
  const [role, setRole] = useState('');
  const [threshold, setThreshold] = useState(3);

  useEffect(() => {
    const handleClick = (e) => { if (colMenuRef.current && !colMenuRef.current.contains(e.target)) setShowColMenu(false); };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const endpoint = HR_REPORT_ENDPOINTS[reportId];
      if (!endpoint) { toast.warn("Report endpoint not configured"); setLoading(false); return; }
      
      const params = { month };
      if (department) params.department = department;
      if (role) params.role = role;
      if (reportId === 'late_frequency') params.threshold = threshold;

      const res = await axios.get(`${API}${endpoint}`, { params, headers: authHeader() });
      
      setData(res.data.data || []);
      setSummary(res.data.summary || null);
      setHiddenCols([]);
    } catch (err) {
      toast.error("Failed to fetch report data.");
    } finally {
      setLoading(false);
    }
  };

  const keys = data.length > 0 ? Object.keys(data[0]) : [];
  const activeKeys = keys.filter(k => !hiddenCols.includes(k));

  return (
    <div className="flex flex-col h-full bg-white dark:bg-[#0d1117] transition-colors">
      <div className="bg-white dark:bg-[#161b22] p-3 md:p-4 border-b border-gray-200 dark:border-[#30363d] shadow-sm flex flex-col gap-3 flex-shrink-0 transition-colors">
        <div className="grid grid-cols-2 md:flex md:flex-row gap-3 w-full items-end">
          <div className="flex flex-col gap-1 w-full md:w-auto">
            <label className="text-[10px] font-bold text-gray-500 dark:text-slate-400 uppercase">Month</label>
            <input type="month" value={month} onChange={e => setMonth(e.target.value)}
              className="w-full md:w-36 pl-3 pr-3 py-1.5 bg-gray-50 dark:bg-[#21262d] border border-gray-300 dark:border-[#30363d] text-gray-700 dark:text-white text-xs rounded-md focus:ring-1 focus:ring-pink-500 outline-none shadow-sm" />
          </div>
          <div className="flex flex-col gap-1 w-full md:w-auto">
            <label className="text-[10px] font-bold text-gray-500 dark:text-slate-400 uppercase">Department</label>
            <input type="text" placeholder="All Depts" value={department} onChange={e => setDepartment(e.target.value)}
              className="w-full md:w-36 pl-3 pr-3 py-1.5 bg-gray-50 dark:bg-[#21262d] border border-gray-300 dark:border-[#30363d] text-gray-700 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 text-xs rounded-md focus:ring-1 focus:ring-pink-500 outline-none shadow-sm" />
          </div>
          <div className="flex flex-col gap-1 w-full md:w-auto">
            <label className="text-[10px] font-bold text-gray-500 dark:text-slate-400 uppercase">Role</label>
            <input type="text" placeholder="All Roles" value={role} onChange={e => setRole(e.target.value)}
              className="w-full md:w-36 pl-3 pr-3 py-1.5 bg-gray-50 dark:bg-[#21262d] border border-gray-300 dark:border-[#30363d] text-gray-700 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 text-xs rounded-md focus:ring-1 focus:ring-pink-500 outline-none shadow-sm" />
          </div>
          {reportId === 'late_frequency' && (
            <div className="flex flex-col gap-1 w-full md:w-auto">
              <label className="text-[10px] font-bold text-gray-500 dark:text-slate-400 uppercase">Late ≥</label>
              <input type="number" min="1" value={threshold} onChange={e => setThreshold(e.target.value)}
                className="w-full md:w-20 pl-3 pr-3 py-1.5 bg-gray-50 dark:bg-[#21262d] border border-gray-300 dark:border-[#30363d] text-gray-700 dark:text-white text-xs rounded-md focus:ring-1 focus:ring-pink-500 outline-none shadow-sm" />
            </div>
          )}
        </div>

        <div className="flex flex-wrap gap-2 items-center w-full justify-start pt-1">
          <button onClick={fetchReport} className="w-full md:w-auto px-4 py-1.5 bg-gray-800 dark:bg-blue-600 text-white text-xs font-medium rounded-md hover:bg-gray-900 dark:hover:bg-blue-700 transition shadow-sm h-[30px]">
            {loading ? 'Loading...' : 'Apply Filters'}
          </button>
          
          {data.length > 0 && (
            <>
              <div className="hidden md:block w-px h-5 bg-gray-300 dark:bg-slate-700 mx-2" />
              <div className="relative" ref={colMenuRef}>
                <button onClick={() => setShowColMenu(!showColMenu)} className="flex-1 md:flex-none justify-center px-3 py-1.5 bg-white dark:bg-[#21262d] border border-gray-300 dark:border-[#30363d] text-gray-700 dark:text-slate-300 text-xs font-medium rounded-md hover:bg-gray-50 dark:hover:bg-slate-800 flex items-center gap-2 shadow-sm transition h-[30px]">
                  <FaColumns /> Columns
                </button>
                {showColMenu && (
                  <div className="absolute left-0 mt-2 w-56 bg-white dark:bg-[#161b22] border border-gray-200 dark:border-[#30363d] shadow-xl rounded-md p-2 z-50 max-h-64 overflow-y-auto">
                    <div className="text-[10px] font-bold text-gray-400 dark:text-slate-500 uppercase mb-2 px-1">Configure Columns</div>
                    {keys.map(k => (
                      <label key={k} className="flex items-center gap-2 px-2 py-1.5 hover:bg-gray-50 dark:hover:bg-slate-800 rounded cursor-pointer text-xs text-gray-700 dark:text-slate-300">
                        <input type="checkbox" checked={!hiddenCols.includes(k)} onChange={() => setHiddenCols(p => p.includes(k) ? p.filter(x => x !== k) : [...p, k])} className="rounded text-pink-600 focus:ring-pink-500 w-3.5 h-3.5" />
                        {k.replace(/_/g,' ')}
                      </label>
                    ))}
                  </div>
                )}
              </div>
              <button onClick={() => exportToCSV(data, reportId, activeKeys)} className="flex-1 md:flex-none justify-center px-3 py-1.5 bg-white dark:bg-[#21262d] border border-gray-300 dark:border-[#30363d] text-gray-700 dark:text-slate-300 text-xs font-medium rounded-md hover:bg-gray-50 dark:hover:bg-slate-800 flex items-center gap-2 shadow-sm transition h-[30px]">
                <FaFileCsv className="text-green-600 dark:text-green-400 text-sm" /> Export CSV
              </button>
              <button onClick={() => exportToExcel(data, reportId, activeKeys)} className="flex-1 md:flex-none justify-center px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-md hover:bg-blue-700 flex items-center gap-2 shadow-sm transition h-[30px]">
                <FaFileExcel className="text-sm" /> Export Excel
              </button>
            </>
          )}
        </div>
      </div>

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-3 bg-gray-50 dark:bg-[#161b22] border-b border-gray-200 dark:border-[#30363d] shrink-0 transition-colors">
          <div className="bg-white dark:bg-[#161b22] rounded-lg border border-gray-200 dark:border-[#30363d] p-3 text-center shadow-sm">
            <p className="text-[10px] text-gray-400 dark:text-slate-500 uppercase font-bold">Records</p>
            <p className="text-lg font-bold text-gray-800 dark:text-white">{summary.totalCount}</p>
          </div>
          {Object.entries(summary).filter(([k]) => k !== 'totalCount').map(([k, v]) => (
            <div key={k} className="bg-white dark:bg-[#161b22] rounded-lg border border-gray-200 dark:border-[#30363d] p-3 text-center shadow-sm">
              <p className="text-[10px] text-gray-400 dark:text-slate-500 uppercase font-bold">{k.replace(/_/g,' ')}</p>
              <p className="text-lg font-bold text-pink-600 dark:text-pink-400">{fmt(v)}</p>
            </div>
          ))}
        </div>
      )}

      <div className="flex-1 overflow-auto p-3 bg-gray-50 dark:bg-[#0d1117]">
        {loading ? (
          <div className="flex items-center justify-center h-48"><div className="w-8 h-8 border-2 border-pink-500 border-t-transparent rounded-full animate-spin" /></div>
        ) : data.length > 0 ? (
          <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-[#30363d] rounded-lg shadow-sm overflow-auto transition-colors">
            <table className="min-w-full text-xs text-left text-gray-600 dark:text-slate-300">
              <thead className="bg-gray-50 dark:bg-[#21262d] text-[10px] uppercase text-gray-400 dark:text-slate-400 font-bold border-b border-gray-200 dark:border-[#30363d] sticky top-0">
                <tr>{activeKeys.map(k => <th key={k} className="px-3 py-2 whitespace-nowrap">{k.replace(/_/g,' ')}</th>)}</tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-[#1E293B]">
                {data.map((row, i) => (
                  <tr key={i} className="hover:bg-pink-50/20 dark:hover:bg-slate-800/50 transition">
                    {activeKeys.map((k, j) => (
                      <td key={j} className="px-3 py-2 whitespace-nowrap">{renderCell(row[k])}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center h-48 text-gray-400 dark:text-slate-500 gap-2 bg-white dark:bg-[#161b22] border border-gray-200 dark:border-[#30363d] rounded-lg shadow-sm transition-colors">
            <FaTable className="text-4xl opacity-20" />
            <p className="text-sm">Apply filters to load report data.</p>
          </div>
        )}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────
// MAIN REPORTS PAGE
// ─────────────────────────────────────────────
const ReportsPage = () => {
  const [loading, setLoading] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeView, setActiveView] = useState("grid");
  const [selectedModule, setSelectedModule] = useState(null);
  const [selectedReport, setSelectedReport] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [previewData, setPreviewData] = useState([]);
  const [summaryData, setSummaryData] = useState(null);
  const [allReportKeys, setAllReportKeys] = useState([]);
  const [hiddenCols, setHiddenCols] = useState([]);
  const [showColMenu, setShowColMenu] = useState(false);
  const [kpiMonth, setKpiMonth] = useState(getLastMonth());
  const colMenuRef = useRef(null);

  useEffect(() => {
    const h = (e) => { if (colMenuRef.current && !colMenuRef.current.contains(e.target)) setShowColMenu(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const getStatusOptions = () => {
    if (selectedReport && STATUS_OPTIONS[selectedReport]) return STATUS_OPTIONS[selectedReport];
    if (selectedModule && STATUS_OPTIONS[selectedModule.id]) return STATUS_OPTIONS[selectedModule.id];
    if (selectedModule) {
      const key = Object.keys(STATUS_OPTIONS).find(k => selectedModule.id.includes(k));
      if (key) return STATUS_OPTIONS[key];
    }
    return STATUS_OPTIONS.default;
  };

  const handlePreview = async (reportId) => {
    if (!reportId) return;
    
    const currentSub = selectedModule?.subReports?.find(r => r.id === reportId);
    if (currentSub?.isChart || ['salary_register','pf_statement','esi_statement','late_frequency'].includes(reportId)) {
      setSelectedReport(reportId);
      setPreviewData([]);
      return;
    }
    
    try {
      setLoading('preview');
      setSelectedReport(reportId);
      
      setPreviewData([]);
      setSummaryData(null);
      setAllReportKeys([]);
      setHiddenCols([]);

      const params = { type: reportId, format: 'json' };
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      if (statusFilter !== "All") params.status = statusFilter;

      const res = await axios.get(`${API}/api/reports/${selectedModule.id}`, {
        params, headers: authHeader()
      });
      
      let dataArray = [];
      
      // ✅ CSV PARSER SUPERPOWER: Instantly translate backend text strings into JSON
      if (typeof res.data === 'string' && res.data.includes(',')) {
        const lines = res.data.trim().split('\n');
        if (lines.length > 1) {
          const headers = lines[0].split(',').map(h => h.trim().replace(/^"|"$/g, ''));
          for (let i = 1; i < lines.length; i++) {
            if (!lines[i].trim()) continue;
            // Regex handles commas safely inside quote marks
            const row = lines[i].split(/,(?=(?:(?:[^"]*"){2})*[^"]*$)/);
            const obj = {};
            headers.forEach((h, idx) => {
              obj[h] = row[idx] ? row[idx].trim().replace(/^"|"$/g, '') : '';
            });
            dataArray.push(obj);
          }
        }
      } 
      // Safe JSON Array fallback
      else if (Array.isArray(res.data)) { dataArray = res.data; }
      else if (res.data && Array.isArray(res.data.data)) { dataArray = res.data.data; }
      else if (res.data && Array.isArray(res.data.reports)) { dataArray = res.data.reports; }
      else if (res.data && Array.isArray(res.data.records)) { dataArray = res.data.records; }
      
      setPreviewData(dataArray);
      setSummaryData(res.data?.summary || null);
      if (dataArray.length > 0) { setAllReportKeys(Object.keys(dataArray[0])); }
      
    } catch (err) {
      setPreviewData([]);
      setAllReportKeys([]);
      if (err.response && err.response.status === 404) {
        toast.info(`The ${selectedModule?.label} reports are currently under construction.`);
      } else {
        toast.error("Could not load preview. Please check your database connection.");
      }
    } finally {
      setLoading(null);
    }
  };

  const handleDownload = async (format) => {
    if (!selectedReport) return;
    try {
      setLoading('export');
      const fileName = `${selectedModule.id}_${selectedReport}_${new Date().toISOString().slice(0,10)}`;
      const activeColumns = allReportKeys.filter(k => !hiddenCols.includes(k));
      
      const params = { format, type: selectedReport, columns: activeColumns.join(',') };
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      if (statusFilter !== "All") params.status = statusFilter;

      const res = await axios.get(`${API}/api/reports/${selectedModule.id}`, {
        params, headers: authHeader()
      });
      
      let exportData = [];
      if (typeof res.data === 'string' && res.data.includes(',')) {
         exportData = previewData; // Use our auto-parsed table data instead of making a messy text blob
      } else if (Array.isArray(res.data)) {
         exportData = res.data;
      } else if (res.data && Array.isArray(res.data.data)) {
         exportData = res.data.data;
      }

      if (format === 'csv') exportToCSV(exportData, fileName, activeColumns);
      else exportToExcel(exportData, fileName, activeColumns);
      
    } catch {
      toast.error("Export failed. API might not support direct download formatting.");
    } finally {
      setLoading(null);
    }
  };

  const filteredModules = REPORT_MODULES.filter(m => m.label.toLowerCase().includes(searchTerm.toLowerCase()));
  const activeKeys = allReportKeys.filter(k => !hiddenCols.includes(k));

  const isHRModule = selectedModule?.isHR;
  const currentSub = selectedModule?.subReports?.find(r => r.id === selectedReport);
  const isChartReport = currentSub?.isChart;
  const isHRTableReport = ['salary_register','pf_statement','esi_statement','late_frequency'].includes(selectedReport);

  const renderHRContent = () => {
    if (selectedReport === 'kpi_dashboard') return <KpiDashboard month={kpiMonth} onMonthChange={setKpiMonth} />;
    if (selectedReport === 'absenteeism')   return <AbsenteeismChart filters={{}} />;
    if (selectedReport === 'labour_cost')   return <LabourCostChart filters={{ month: kpiMonth }} />;
    if (isHRTableReport)                    return <HRReportTable reportId={selectedReport} />;
    return null; 
  };

  const hrContent = isHRModule ? renderHRContent() : null;
  const showStandardContent = !isHRModule || (!isChartReport && !isHRTableReport && hrContent === null);

  return (
    <div className="flex flex-col h-screen bg-gray-50 dark:bg-[#0d1117] overflow-hidden font-sans transition-colors">
      <div className="bg-white dark:bg-[#161b22] border-b border-gray-200 dark:border-[#30363d] px-4 md:px-6 py-3 flex justify-between items-center shadow-sm z-10 h-16 flex-shrink-0 transition-colors">
        <div className="flex items-center gap-3 md:gap-4">
          {activeView === "report" && (
            <button onClick={() => { setActiveView("grid"); setPreviewData([]); setSelectedReport(null); setAllReportKeys([]); }}
              className="p-1.5 md:p-2 rounded-full hover:bg-gray-100 dark:hover:bg-slate-800 text-gray-600 dark:text-slate-300 transition">
              <FaArrowLeft />
            </button>
          )}
          <div>
            <h1 className="text-sm md:text-base font-bold text-gray-800 dark:text-white tracking-tight">
              {activeView === "report" && selectedModule ? selectedModule.label : "Analytics & Reporting"}
            </h1>
            <p className="hidden md:block text-xs text-gray-500 dark:text-slate-400">
              {activeView === "report" ? "Advanced Data View" : "Enterprise Data Intelligence"}
            </p>
          </div>
        </div>
        {activeView === "grid" && (
          <div className="relative w-48 md:w-64">
            <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-slate-500 text-xs" />
            <input type="text" placeholder="Search reports..." value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-3 py-1.5 border border-gray-300 dark:border-[#30363d] bg-white dark:bg-[#21262d] text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 rounded-md text-sm focus:ring-1 focus:ring-blue-500 outline-none transition" />
          </div>
        )}
      </div>

      {activeView === "grid" && (
        <div className="flex-1 overflow-y-auto p-4 md:p-6">
          <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2.5 sm:gap-4">
            {filteredModules.map(module => (
              <div key={module.id}
                onClick={() => {
                  setSelectedModule(module);
                  setActiveView("report");
                  const first = module.subReports[0].id;
                  setSelectedReport(first);
                  if (!module.isHR) handlePreview(first);
                  else setSelectedReport(first);
                }}
                className="bg-white dark:bg-[#161b22] rounded-xl border border-gray-200 dark:border-[#30363d] shadow-2xs hover:shadow-xs transition-all cursor-pointer group flex flex-col overflow-hidden">
                <div className="p-2.5 sm:p-4 flex items-center gap-2 sm:gap-3 border-b border-gray-100 dark:border-[#30363d]">
                  <div className={`p-1.5 sm:p-2 rounded-lg ${module.color} shadow-2xs group-hover:scale-105 transition-transform shrink-0`}>{module.icon}</div>
                  <div className="min-w-0">
                    <h3 className="font-bold text-gray-800 dark:text-white text-xs sm:text-base group-hover:text-blue-600 transition-colors truncate">{module.label}</h3>
                    <p className="text-[9px] sm:text-[10px] text-gray-400 dark:text-slate-400 leading-tight mt-0.5 truncate">{module.desc}</p>
                  </div>
                </div>
                <div className="bg-gray-50 px-4 py-2 flex justify-between items-center">
                  <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">{module.subReports.length} Reports</span>
                  <div className="text-gray-300 text-xs group-hover:text-blue-500 group-hover:translate-x-1 transition-all">Open &rarr;</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeView === "report" && selectedModule && (
        <div className="flex flex-col md:flex-row flex-1 overflow-hidden">
          <div className="w-full md:w-64 bg-white dark:bg-[#161b22] border-b md:border-r md:border-b-0 border-gray-200 dark:border-[#30363d] flex flex-col flex-shrink-0 z-10 transition-colors">
            <div className="p-3 md:p-4 border-b border-gray-100 dark:border-[#30363d] hidden md:block">
              <label className="text-[10px] font-bold text-gray-400 dark:text-slate-400 uppercase tracking-wider">Reports</label>
            </div>
            <div className="flex md:flex-col overflow-x-auto md:overflow-y-auto p-2 gap-2 md:gap-1 scrollbar-hide">
              {selectedModule.subReports.map(report => (
                <button key={report.id}
                  onClick={() => {
                    setSelectedReport(report.id);
                    if (!isHRModule || (!report.isChart && !['salary_register','pf_statement','esi_statement','late_frequency'].includes(report.id))) {
                      handlePreview(report.id);
                    } else {
                      setSelectedReport(report.id); 
                      setPreviewData([]); 
                    }
                  }}
                  className={`flex-shrink-0 md:w-full text-left px-3 py-2 md:py-2.5 rounded-md text-xs font-medium transition flex items-center justify-between ${
                    selectedReport === report.id
                      ? "bg-blue-50 text-blue-700 border border-blue-200"
                      : "text-gray-600 hover:bg-gray-50 border border-transparent"
                  }`}>
                  <span className="whitespace-nowrap">{report.label}</span>
                  {selectedReport === report.id && <div className="hidden md:block w-1.5 h-1.5 rounded-full bg-blue-500 ml-2" />}
                </button>
              ))}
            </div>
          </div>

          <div className="flex-1 flex flex-col overflow-hidden bg-gray-50">
            {isHRModule && hrContent && (
              <div className="flex-1 overflow-auto">
                {hrContent}
              </div>
            )}

            {showStandardContent && (
              <>
                <div className="bg-white p-3 md:p-4 border-b border-gray-200 shadow-sm flex flex-col gap-3 flex-shrink-0">
                  <div className="grid grid-cols-2 md:flex md:flex-row gap-3 w-full items-end">
                    <div className="flex flex-col gap-1 w-full md:w-auto">
                      <label className="text-[10px] font-bold text-gray-500 uppercase">From</label>
                      <div className="relative">
                        <FaCalendarAlt className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs" />
                        <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
                          className="w-full md:w-36 pl-8 pr-3 py-1.5 bg-gray-50 border border-gray-300 text-gray-700 text-xs rounded-md focus:ring-1 focus:ring-blue-500 outline-none shadow-sm" />
                      </div>
                    </div>
                    <div className="flex flex-col gap-1 w-full md:w-auto">
                      <label className="text-[10px] font-bold text-gray-500 uppercase">To</label>
                      <div className="relative">
                        <FaCalendarAlt className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs" />
                        <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
                          className="w-full md:w-36 pl-8 pr-3 py-1.5 bg-gray-50 border border-gray-300 text-gray-700 text-xs rounded-md focus:ring-1 focus:ring-blue-500 outline-none shadow-sm" />
                      </div>
                    </div>
                    <div className="flex flex-col gap-1 w-full col-span-2 md:col-span-1 md:w-auto">
                      <label className="text-[10px] font-bold text-gray-500 uppercase">Status</label>
                      <div className="relative">
                        <FaFilter className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs" />
                        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
                          className="w-full md:w-40 pl-8 pr-8 py-1.5 bg-gray-50 dark:bg-[#21262d] border border-gray-300 dark:border-[#30363d] text-gray-700 dark:text-white text-xs rounded-md focus:ring-1 focus:ring-blue-500 outline-none shadow-sm appearance-none">
                          <option value="All">All Statuses</option>
                          {getStatusOptions().map(opt => <option key={opt} value={opt}>{opt}</option>)}
                        </select>
                        <FaChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 dark:text-slate-500 text-[10px]" />
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 items-center w-full justify-start pt-1">
                    <button onClick={() => handlePreview(selectedReport)}
                      className="w-full md:w-auto px-4 py-1.5 bg-gray-800 dark:bg-blue-600 text-white text-xs font-medium rounded-md hover:bg-gray-900 dark:hover:bg-blue-700 transition shadow-sm h-[30px]">
                      Apply Filters
                    </button>
                    
                    {previewData.length > 0 && (
                      <>
                        <div className="hidden md:block w-px h-5 bg-gray-300 dark:bg-slate-700 mx-2" />
                        <div className="relative" ref={colMenuRef}>
                          <button onClick={() => setShowColMenu(!showColMenu)}
                            className="flex-1 md:flex-none justify-center px-3 py-1.5 bg-white dark:bg-[#21262d] border border-gray-300 dark:border-[#30363d] text-gray-700 dark:text-slate-300 text-xs font-medium rounded-md hover:bg-gray-50 dark:hover:bg-slate-800 flex items-center gap-2 shadow-sm transition h-[30px]">
                            <FaColumns /> Columns
                          </button>
                          {showColMenu && (
                            <div className="absolute left-0 mt-2 w-56 bg-white dark:bg-[#161b22] border border-gray-200 dark:border-[#30363d] shadow-xl rounded-md p-2 z-50 max-h-64 overflow-y-auto">
                              <div className="text-[10px] font-bold text-gray-400 dark:text-slate-500 uppercase mb-2 px-1">Configure Columns</div>
                              {allReportKeys.map(key => (
                                <label key={key} className="flex items-center gap-2 px-2 py-1.5 hover:bg-gray-50 dark:hover:bg-slate-800 rounded cursor-pointer text-xs text-gray-700 dark:text-slate-300">
                                  <input type="checkbox" checked={!hiddenCols.includes(key)}
                                    onChange={() => setHiddenCols(p => p.includes(key) ? p.filter(k => k !== key) : [...p, key])}
                                    className="rounded text-blue-600 focus:ring-blue-500 w-3.5 h-3.5" />
                                  <span className="truncate">{key.replace(/_/g,' ')}</span>
                                </label>
                              ))}
                            </div>
                          )}
                        </div>
                        <button onClick={() => handleDownload("csv")} disabled={loading}
                          className="flex-1 md:flex-none justify-center px-3 py-1.5 bg-white dark:bg-[#21262d] border border-gray-300 dark:border-[#30363d] text-gray-700 dark:text-slate-300 text-xs font-medium rounded-md hover:bg-gray-50 dark:hover:bg-slate-800 flex items-center gap-2 shadow-sm transition h-[30px]">
                          <FaFileCsv className="text-green-600 dark:text-green-400 text-sm" /> Export CSV
                        </button>
                        <button onClick={() => handleDownload("excel")} disabled={loading}
                          className="flex-1 md:flex-none justify-center px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-md hover:bg-blue-700 flex items-center gap-2 shadow-sm transition h-[30px]">
                          <FaFileExcel className="text-sm" /> Export Excel
                        </button>
                      </>
                    )}
                  </div>
                </div>

                <div className="flex-1 overflow-auto p-3 md:p-4 bg-gray-50 dark:bg-[#0d1117]">
                  {summaryData && (
                    <div className="mb-4 grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div className="bg-white dark:bg-[#161b22] p-3 rounded-md border border-gray-200 dark:border-[#30363d] shadow-sm transition-colors">
                        <p className="text-[10px] text-gray-500 dark:text-slate-400 uppercase font-bold">Total Records</p>
                        <p className="text-base md:text-lg font-bold text-gray-800 dark:text-white">{summaryData.totalCount}</p>
                      </div>
                      {summaryData.totalValue && (
                        <div className="bg-white dark:bg-[#161b22] p-3 rounded-md border border-gray-200 dark:border-[#30363d] shadow-sm transition-colors">
                          <p className="text-[10px] text-gray-500 dark:text-slate-400 uppercase font-bold">Total Value</p>
                          <p className="text-base md:text-lg font-bold text-blue-600 dark:text-blue-400">{summaryData.totalValue}</p>
                        </div>
                      )}
                    </div>
                  )}

                  {loading === 'preview' ? (
                    <div className="flex flex-col items-center justify-center h-64 gap-3 bg-white dark:bg-[#161b22] border border-gray-200 dark:border-[#30363d] rounded-lg shadow-sm transition-colors">
                      <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                      <p className="text-xs text-gray-500 dark:text-slate-400">Processing Data...</p>
                    </div>
                  ) : previewData.length > 0 ? (
                    <>
                      <div className="md:hidden flex flex-col gap-3 min-h-[400px]">
                        {previewData.map((row, idx) => (
                          <div key={idx} className="bg-white border border-gray-200 rounded-lg shadow-sm p-3 hover:border-blue-300 transition-colors">
                            <div className="text-sm font-bold text-gray-800 border-b border-gray-100 pb-2 mb-2">
                              {activeKeys.length > 0 ? renderCell(row[activeKeys[0]]) : `Record #${idx + 1}`}
                            </div>
                            <div className="flex flex-col gap-2">
                              {activeKeys.slice(1).map((key, i) => (
                                <div key={i} className="flex justify-between items-start gap-3 text-xs">
                                  <span className="text-gray-500 font-medium shrink-0">{key.replace(/_/g,' ')}:</span>
                                  <span className="text-gray-800 text-right break-words">{renderCell(row[key])}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="hidden md:block bg-white border border-gray-200 rounded-lg shadow-sm overflow-auto min-h-[400px]">
                        <table className="min-w-full text-xs text-left text-gray-600">
                          <thead className="bg-gray-50 text-[10px] uppercase text-gray-500 font-bold border-b border-gray-200 sticky top-0">
                            <tr>{activeKeys.map(key => <th key={key} className="px-4 py-3 whitespace-nowrap">{key.replace(/_/g,' ')}</th>)}</tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {previewData.map((row, idx) => (
                              <tr key={idx} className="hover:bg-blue-50/30 transition">
                                {activeKeys.map((key, i) => (
                                  <td key={i} className="px-4 py-2.5 whitespace-nowrap">{renderCell(row[key])}</td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  ) : (
                    <div className="flex flex-col items-center justify-center h-64 text-gray-400 gap-2 bg-white border border-gray-200 rounded-lg shadow-sm">
                      <FaTable className="text-4xl opacity-20" />
                      <p className="text-sm">No data found for the selected criteria.</p>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}

    </div>
  );
};

export default ReportsPage;